import React, {useCallback, useEffect, useMemo, useState} from "react";
import {routes} from "../../../../core/models/static/routes/app-routes";
import CacheService from "../../../../core/services/cache/cache_service";
import useIsMounted from "../../../hooks/use-is-mounted";
import {Typography, useTheme} from "@mui/material";
import {useHistory, useLocation} from "react-router-dom";
import {useConfigureNotebook} from "../../../hooks/use-notebook-config";
import useQuery from "../../../hooks/use-query";


const AccessDeniedView = ({root = false}) => {
    const theme = useTheme();
    const history = useHistory();
    const location = useLocation();
    const [configUrls, setConfigUrls] = useState(CacheService.getUrlConfig());
    const isMounted = useIsMounted();
    const [query] = useQuery();
    const hideBackButton = useMemo(() => query.hideBackButton, [query.hideBackButton]);

    const {toggleShow: toggleShowNotebooksAction} = useConfigureNotebook('auth-routes');

    /**
     * As soon as the component is mounted, it hides the notebooks feature
     */
    useEffect(() => {
        toggleShowNotebooksAction(false);
    }, [toggleShowNotebooksAction])

    /**
     * Fetches the login url from the api
     * @type {(function(): Promise<void>)}
     */
    const fetchLoginUrl = useCallback(async () => {
        const urlConfig = await CacheService.getOrFetchUrlConfig();
        if (!isMounted())
            return;
        setConfigUrls(urlConfig);
    }, [isMounted]);

    /**
     * Checks whether the login / logout url exists, otherwise gets them from api call
     */
    useEffect(() => {
        if (!configUrls) {
            void fetchLoginUrl();
        }
    }, [fetchLoginUrl, configUrls]);

    /**
     * checks the pathname of the url based on accessDeniedForSection route,
     * no prefix => change the url to access denied for section
     * prefix of routes.auth.base => change the url to access denied for section
     *
     *
     * Checks whether the login / logout url exists, otherwise gets them from api call
     */
    useEffect(() => {
        if (location.pathname === routes.error.accessDeniedForSection || location.pathname.includes(routes.auth.base)) {
            history.replace(routes.error.accessDenied);
        }
    }, [history, isMounted, location.pathname])

    /**
     * Redirects the user to the main url of core
     */
    const redirectToCore = useCallback(() => {
        if (configUrls?.url) {
            window.location.replace(configUrls?.url);
        }

    }, [configUrls]);


    /**
     * retries the page that caused the error
     * @type {() => void}
     */
    const retry = useCallback(() => {
        history.goBack();
    }, [history]);

    /**
     * goes back to the previous page form the erroneous page
     * @type {() => void}
     */
    const goBack = useCallback(() => {
        if (history.length > 2) {
            history.go(-2);
        } else {
            window.close();
        }
    }, [history]);

    return (
        <div
            className={`error-page ${root ? "root" : ''} row no-gutters flex-column flex-md-row justify-content-around justify-content-md-around align-items-center`}>
            <svg width="380px" height="500px" viewBox="0 0 837 1045" version="1.1"
                 xmlns="http://www.w3.org/2000/svg">
                <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                    <path
                        d="M353,9 L626.664028,170 L626.664028,487 L353,642 L79.3359724,487 L79.3359724,170 L353,9 Z"
                        id="Polygon-1" stroke={theme.palette.primary.main} strokeWidth="6"/>
                    <path
                        d="M78.5,529 L147,569.186414 L147,648.311216 L78.5,687 L10,648.311216 L10,569.186414 L78.5,529 Z"
                        id="Polygon-2" stroke={theme.palette.tertiary.main} strokeWidth="6"/>
                    <path
                        d="M773,186 L827,217.538705 L827,279.636651 L773,310 L719,279.636651 L719,217.538705 L773,186 Z"
                        id="Polygon-3" stroke={theme.palette.secondary.main} strokeWidth="6"/>
                    <path
                        d="M639,529 L773,607.846761 L773,763.091627 L639,839 L505,763.091627 L505,607.846761 L639,529 Z"
                        id="Polygon-4" stroke={theme.palette.tertiary.main} strokeWidth="6"/>
                    <path
                        d="M281,801 L383,861.025276 L383,979.21169 L281,1037 L179,979.21169 L179,861.025276 L281,801 Z"
                        id="Polygon-5" stroke={theme.palette.secondary.main} strokeWidth="6"/>
                </g>
            </svg>
            <div className="message-box">
                <h1>403</h1>
                <Typography component={'p'} variant={'body2'}>Access Denied. Sorry, but your are not allowed in here. Please Contact IT.</Typography>
                <div className="action-link-wrap">
                    {
                        !root &&
                        <button onClick={retry} className='animated-button btn'>
                            <span>Sorry Again!</span><span>Retry</span>
                        </button>
                    }
                    {
                        !root && !hideBackButton &&
                        <button onClick={goBack} className='animated-button btn'>
                            <span>Sorry Again!</span><span>Go Back</span>
                        </button>
                    }
                    {
                        !!root && !hideBackButton &&
                        <button onClick={redirectToCore} className='animated-button btn'>
                            <span>Sorry Again!</span><span>Go to Core</span>
                        </button>
                    }
                </div>
            </div>
        </div>
    );
}

export default AccessDeniedView;
